import type { ComponentChildren } from 'preact'
import { X } from 'lucide-preact'
import { useEffect, useRef, useState } from 'preact/hooks'

export interface DialogProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: ComponentChildren
  className?: string
  disableMobileBehavior?: boolean
  closeOnBackdropClick?: boolean
  size?: 'sm' | 'md' | 'lg' | 'xl'
  footer?: ComponentChildren
}

export function Dialog({
  isOpen,
  onClose,
  title,
  children,
  className = '',
  disableMobileBehavior = false,
  closeOnBackdropClick,
  size = 'lg',
  footer,
}: DialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const [isAnimating, setIsAnimating] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)

  const sizeClasses: Record<string, string> = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-5xl',
    xl: 'sm:max-w-[85rem]',
  }

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
      setTimeout(() => setIsAnimating(true), 10)
      document.body.style.overflow = 'hidden'
    } else {
      setIsAnimating(false)
      setTimeout(() => setShouldRender(false), 200)
      document.body.style.overflow = 'auto'
    }

    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [isOpen])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  const handleBackdropClick = (event: MouseEvent) => {
    if (closeOnBackdropClick && event.target === dialogRef.current) {
      onClose()
    }
  }

  if (!shouldRender) return null

  return (
    <div
      ref={dialogRef}
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black transition-opacity duration-200 ${
        isAnimating ? 'bg-opacity-50' : 'bg-opacity-0'
      }`}
      onClick={handleBackdropClick}
    >
      <div
        className={`bg-white shadow-xl transition-all duration-200 ease-out
          ${
            disableMobileBehavior
              ? 'max-w-5xl w-full max-h-[90dvh] relative inset-auto rounded-3xl m-auto flex flex-col'
              : `fixed inset-0 w-full max-w-full rounded-none max-h-none m-0 flex flex-col sm:mx-10 ${sizeClasses[size]} sm:w-full sm:max-h-[90dvh] sm:relative sm:inset-auto sm:rounded-3xl sm:m-auto`
          }
          ${isAnimating ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'}
          ${className}`}
        onClick={e => e.stopPropagation()}
      >
        {title && (
          <div className='relative px-8 py-6 border-b border-gray-200 shrink-0'>
            <h3 className='text-lg font-semibold text-cencert-header-blue pr-8'>
              {title}
            </h3>
            <button
              onClick={onClose}
              className='absolute top-6 right-8 text-gray-500 hover:text-gray-700 transition-colors'
              aria-label='Zamknij'
            >
              <X size={20} strokeWidth={1} />
            </button>
          </div>
        )}
        <div className='flex-1 overflow-auto'>{children}</div>
        {footer && (
          <div className='px-10 py-6 border-gray-200 shrink-0'>{footer}</div>
        )}
      </div>
    </div>
  )
}
